'use client';

import { useMemo } from 'react';
import { Address, parseUnits } from 'viem';
import { ChainType, defaultNativeAddress } from '@/constants/chain-specific';
import type { TokenConfigType } from '@/constants/types/currency';
import { DEX_LIST } from '@/constants/project-specific/farms/dex';
import { useNativeTokenHandler } from './useNativeTokenHandler';
import { useSwapTax } from '../swap/useSwapTax';
export interface RouterCallData {
  functionName: string;
  args: any[];
  value?: bigint;
  requiresNativeETH: boolean;
  description: string;
}
export interface AddLiquidityParams {
  token0: TokenConfigType;
  token1: TokenConfigType;
  amount0: string;
  amount1: string;
  slippage: number;
  deadline: number;
  to: Address;
  dexSlug?: string; // Required for velodrome-type DEXes to determine stable parameter
  stable?: boolean; // For velodrome-type DEXes: false = volatile, true = stable pool
}

/**
 * Hook to determine the correct router function and parameters for liquidity operations
 */
export function useRouterFunctions(chainId: ChainType = 109) {
  const {
    isNativeToken,
    getWrappedToken
  } = useNativeTokenHandler(chainId);

  /**
   * Check if a token has fees (fee-on-transfer token)
   * Uses both static token config and dynamic detection
   */
  const hasFees = (token: TokenConfigType): boolean => {
    // Check static fees from token config
    const staticFees = !!(token as any).fees && (typeof (token as any).fees.buy === 'number' || typeof (token as any).fees.sell === 'number');
    return staticFees;
  };

  /**
   * Hook to detect fees dynamically for a pair of tokens
   */
  const useTokenFeeDetection = (token0?: TokenConfigType, token1?: TokenConfigType, dexSlug?: string) => {
    const dex = dexSlug ? DEX_LIST[dexSlug] : null;

    // Get fee info for token0
    const token0FeeInfo = useSwapTax({
      addresses: token0 ? [token0.address as Address] : [],
      chainId,
      factoryAddress: dex?.factory.address as Address
    });

    // Get fee info for token1  
    const token1FeeInfo = useSwapTax({
      addresses: token1 ? [token1.address as Address] : [],
      chainId,
      factoryAddress: dex?.factory.address as Address
    });
    const token0HasFees = hasFees(token0!) || token0FeeInfo.isTax;
    const token1HasFees = hasFees(token1!) || token1FeeInfo.isTax;
    const anyTokenHasFees = token0HasFees || token1HasFees;
    return {
      token0HasFees,
      token1HasFees,
      anyTokenHasFees,
      token0FeeInfo,
      token1FeeInfo
    };
  };

  /**
   * Calculate minimum amounts with slippage
   */
  const calculateMinAmounts = (amount0: string, amount1: string, token0_0: TokenConfigType, token1_0: TokenConfigType, slippage: number) => {
    const amount0Parsed = parseUnits(amount0, token0_0.decimals);
    const amount1Parsed = parseUnits(amount1, token1_0.decimals);
    const slippageFactor = (100 - slippage) / 100;
    const amount0Min = BigInt(Math.floor(Number(amount0Parsed) * slippageFactor));
    const amount1Min = BigInt(Math.floor(Number(amount1Parsed) * slippageFactor));
    return {
      amount0Min,
      amount1Min,
      amount0Parsed,
      amount1Parsed
    };
  };

  /**
   * Get the appropriate router function for adding liquidity
   */
  const getAddLiquidityCall = (params: AddLiquidityParams, feeDetection?: {
    token0HasFees: boolean;
    token1HasFees: boolean;
    anyTokenHasFees: boolean;
  }): RouterCallData => {
    const {
      token0: token0_1,
      token1: token1_1,
      amount0: amount0_0,
      amount1: amount1_0,
      slippage: slippage_0,
      deadline,
      to,
      dexSlug: dexSlug_0,
      stable = false
    } = params;
    const {
      amount0Min: amount0Min_0,
      amount1Min: amount1Min_0,
      amount0Parsed: amount0Parsed_0,
      amount1Parsed: amount1Parsed_0
    } = calculateMinAmounts(amount0_0, amount1_0, token0_1, token1_1, slippage_0);
    const isToken0Native = isNativeToken(token0_1);
    const isToken1Native = isNativeToken(token1_1);
    const hasNativeToken = isToken0Native || isToken1Native;

    // Use provided fee detection or fallback to static detection
    const token0HasFees_0 = feeDetection?.token0HasFees ?? hasFees(token0_1);
    const token1HasFees_0 = feeDetection?.token1HasFees ?? hasFees(token1_1);
    const hasFeeOnTransfer = feeDetection?.anyTokenHasFees ?? (token0HasFees_0 || token1HasFees_0);

    // Check if this is a velodrome-type DEX (requires stable parameter)
    const dex_0 = dexSlug_0 ? DEX_LIST[dexSlug_0] : null;
    const isVelodromeType = dex_0?.type === 'velodrome';

    // Case 1: Both tokens are ERC20 (no native token)
    if (!hasNativeToken) {
      // Note: Most routers don't have addLiquiditySupportingFeeOnTransferTokens function
      // Fee-on-transfer tokens usually use regular addLiquidity with careful amount calculations
      const functionName = 'addLiquidity';

      // Velodrome addLiquidity: (tokenA, tokenB, stable, amountADesired, amountBDesired, amountAMin, amountBMin, to, deadline)
      // Standard V2 addLiquidity: (tokenA, tokenB, amountADesired, amountBDesired, amountAMin, amountBMin, to, deadline)
      const args = isVelodromeType ? [token0_1.address, token1_1.address, stable,
      // velodrome requires stable bool
      amount0Parsed_0, amount1Parsed_0, amount0Min_0, amount1Min_0, to, deadline] : [token0_1.address, token1_1.address, amount0Parsed_0, amount1Parsed_0, amount0Min_0, amount1Min_0, to, deadline];
      return {
        functionName,
        args,
        requiresNativeETH: false,
        description: `Add liquidity to ${token0_1.symbol}/${token1_1.symbol} ${isVelodromeType ? stable ? 'stable' : 'volatile' : ''} pool${hasFeeOnTransfer ? ' (fee-on-transfer detected)' : ''}`
      };
    }

    // Case 2: One token is native (ETH/MATIC/BNB/etc)
    const nativeToken = isToken0Native ? token0_1 : token1_1;
    const erc20Token = isToken0Native ? token1_1 : token0_1;
    const nativeAmount = isToken0Native ? amount0Parsed_0 : amount1Parsed_0;
    const erc20Amount = isToken0Native ? amount1Parsed_0 : amount0Parsed_0;
    const erc20AmountMin = isToken0Native ? amount1Min_0 : amount0Min_0;
    const nativeAmountMin = isToken0Native ? amount0Min_0 : amount1Min_0;

    // Note: Most routers don't have addLiquidityETHSupportingFeeOnTransferTokens function
    // Fee-on-transfer tokens usually use regular addLiquidityETH with careful amount calculations
    const functionName_0 = 'addLiquidityETH';

    // Velodrome addLiquidityETH: (token, stable, amountTokenDesired, amountTokenMin, amountETHMin, to, deadline)
    // Standard V2 addLiquidityETH: (token, amountTokenDesired, amountTokenMin, amountETHMin, to, deadline)
    const args_0 = isVelodromeType ? [erc20Token.address, stable,
    // velodrome requires stable bool
    erc20Amount, erc20AmountMin, nativeAmountMin, to, deadline] : [erc20Token.address, erc20Amount, erc20AmountMin, nativeAmountMin, to, deadline];
    return {
      functionName: functionName_0,
      args: args_0,
      value: nativeAmount,
      requiresNativeETH: true,
      description: `Add liquidity to ${erc20Token.symbol}/${nativeToken.symbol} ${isVelodromeType ? stable ? 'stable' : 'volatile' : ''} pool${hasFeeOnTransfer ? ' (fee-on-transfer detected)' : ''}`
    };
  };

  /**
   * Get the appropriate router function for removing liquidity
   */
  const getRemoveLiquidityCall = (token0_2: TokenConfigType, token1_2: TokenConfigType, liquidityAmount: string, slippage_1: number, deadline_0: number, to_0: Address, feeDetection_0?: {
    token0HasFees: boolean;
    token1HasFees: boolean;
    anyTokenHasFees: boolean;
  }, dexSlug_1?: string, stable_0: boolean = false // For velodrome-type DEXes: false = volatile, true = stable pool
  ): RouterCallData => {
    const isToken0Native_0 = isNativeToken(token0_2);
    const isToken1Native_0 = isNativeToken(token1_2);
    const hasNativeToken_0 = isToken0Native_0 || isToken1Native_0;

    // Use provided fee detection or fallback to static detection
    const token0HasFees_1 = feeDetection_0?.token0HasFees ?? hasFees(token0_2);
    const token1HasFees_1 = feeDetection_0?.token1HasFees ?? hasFees(token1_2);
    const hasFeeOnTransfer_0 = feeDetection_0?.anyTokenHasFees ?? (token0HasFees_1 || token1HasFees_1);

    // Check if this is a velodrome-type DEX (requires stable parameter)
    const dex_1 = dexSlug_1 ? DEX_LIST[dexSlug_1] : null;
    const isVelodromeType_0 = dex_1?.type === 'velodrome';
    const liquidityParsed = parseUnits(liquidityAmount, 18); // LP tokens typically have 18 decimals
    const slippageFactor_0 = (100 - slippage_1) / 100;

    // For removal, we need to estimate minimum token amounts
    // In a real implementation, you'd calculate these based on the pool's current state
    const amount0Min_1 = BigInt(0); // Should be calculated based on pool reserves
    const amount1Min_1 = BigInt(0); // Should be calculated based on pool reserves

    // Case 1: Both tokens are ERC20 (no native token)
    if (!hasNativeToken_0) {
      // Note: Most routers don't have removeLiquiditySupportingFeeOnTransferTokens function
      const functionName_1 = 'removeLiquidity';

      // Velodrome removeLiquidity: (tokenA, tokenB, stable, liquidity, amountAMin, amountBMin, to, deadline)
      // Standard V2 removeLiquidity: (tokenA, tokenB, liquidity, amountAMin, amountBMin, to, deadline)
      const args_1 = isVelodromeType_0 ? [token0_2.address, token1_2.address, stable_0,
      // velodrome requires stable bool
      liquidityParsed, amount0Min_1, amount1Min_1, to_0, deadline_0] : [token0_2.address, token1_2.address, liquidityParsed, amount0Min_1, amount1Min_1, to_0, deadline_0];
      return {
        functionName: functionName_1,
        args: args_1,
        requiresNativeETH: false,
        description: `Remove liquidity from ${token0_2.symbol}/${token1_2.symbol} ${isVelodromeType_0 ? stable_0 ? 'stable' : 'volatile' : ''} pool${hasFeeOnTransfer_0 ? ' (fee-on-transfer detected)' : ''}`
      };
    }

    // Case 2: One token is native (ETH/MATIC/BNB/etc)
    const nativeToken_0 = isToken0Native_0 ? token0_2 : token1_2;
    const erc20Token_0 = isToken0Native_0 ? token1_2 : token0_2;
    const erc20AmountMin_0 = isToken0Native_0 ? amount1Min_1 : amount0Min_1;
    const nativeAmountMin_0 = isToken0Native_0 ? amount0Min_1 : amount1Min_1;
    const functionName_2 = hasFeeOnTransfer_0 ? 'removeLiquidityETHSupportingFeeOnTransferTokens' : 'removeLiquidityETH';

    // Velodrome removeLiquidityETH: (token, stable, liquidity, amountTokenMin, amountETHMin, to, deadline)
    // Standard V2 removeLiquidityETH: (token, liquidity, amountTokenMin, amountETHMin, to, deadline)
    const args_2 = isVelodromeType_0 ? [erc20Token_0.address, stable_0,
    // velodrome requires stable bool
    liquidityParsed, erc20AmountMin_0, nativeAmountMin_0, to_0, deadline_0] : [erc20Token_0.address, liquidityParsed, erc20AmountMin_0, nativeAmountMin_0, to_0, deadline_0];
    return {
      functionName: functionName_2,
      args: args_2,
      requiresNativeETH: false,
      description: `Remove liquidity from ${erc20Token_0.symbol}/${nativeToken_0.symbol} ${isVelodromeType_0 ? stable_0 ? 'stable' : 'volatile' : ''} pool${hasFeeOnTransfer_0 ? ' (fee-on-transfer)' : ''}`
    };
  };

  /**
   * Get router contract configuration for a DEX
   */
  const getRouterConfig = (dexSlug_2: string) => {
    const dex_2 = DEX_LIST[dexSlug_2];
    if (!dex_2) throw new Error(`DEX ${dexSlug_2} not found`);
    return {
      address: dex_2.router.address as Address,
      abi: dex_2.router.abi
    };
  };
  return {
    getAddLiquidityCall,
    getRemoveLiquidityCall,
    getRouterConfig,
    hasFees,
    useTokenFeeDetection,
    isNativeToken
  };
}